import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { TextArea } from '@shared/components/ui/textarea';
import { ModalCenter, type ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shared/components/ui/select';
import { createHttpClient } from '@shared/adapters/HttpClient';
import { apiClientWH } from '@shared/services';
import { useToast } from '@shared/hooks/use-toast';
import { useQueryClient } from '@tanstack/react-query';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { OrdersApiAdapter } from '../../adapters/OrdersApiAdapter';
import { usePaymentMethods } from '../../hooks';

interface ShippedPaymentModalProps {
  orderId?: number;
  currentTotal?: number;
  currentPaymentMethodId?: number;
  onSuccess?: () => void;
}

export const ShippedPaymentModal = forwardRef<ModalCenterRef, ShippedPaymentModalProps>(
  ({ orderId, currentTotal, currentPaymentMethodId, onSuccess }, ref) => {
    const { t } = useTranslation('viewOrdersModule');
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const innerRef = useRef<ModalCenterRef>(null);
    const api = new OrdersApiAdapter(createHttpClient(apiClientWH));
    const { paymentMethods, loading: paymentMethodsLoading } = usePaymentMethods();

    const [total, setTotal] = useState('');
    const [paymentMethodId, setPaymentMethodId] = useState('');
    const [reason, setReason] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    useImperativeHandle(
      ref,
      () => ({
        openModal: () => {
          setTotal(currentTotal !== undefined ? String(currentTotal) : '');
          setPaymentMethodId(currentPaymentMethodId !== undefined ? String(currentPaymentMethodId) : '');
          setReason('');
          innerRef.current?.openModal();
        },
        closeModal: () => {
          innerRef.current?.closeModal();
        },
      }),
      [currentTotal, currentPaymentMethodId],
    );

    const handleClose = () => innerRef.current?.closeModal();

    const handleSave = async () => {
      if (!orderId || !reason.trim() || (!total && !paymentMethodId)) return;
      setIsSubmitting(true);
      try {
        const payload: Parameters<typeof api.patchShippedPaymentDetails>[1] = {
          reason: reason.trim(),
        };
        if (total) payload.total = Number(total);
        if (paymentMethodId) payload.payment_method_id = Number(paymentMethodId);
        await api.patchShippedPaymentDetails(orderId, payload);
        toast({
          title: t('shippedPayment.success.title'),
          description: t('shippedPayment.success.description'),
          variant: 'success',
        });
        await queryClient.invalidateQueries({ queryKey: ['paymentCorrections'] });
        onSuccess?.();
        handleClose();
      } catch (error: any) {
        const msg =
          error?.response?.data?.error?.message ||
          error?.response?.data?.message ||
          t('shippedPayment.error.description');
        toast({
          title: t('shippedPayment.error.title'),
          description: msg,
          variant: 'destructive',
        });
      } finally {
        setIsSubmitting(false);
      }
    };

    const canSubmit = !!reason.trim() && (!!total || !!paymentMethodId);

    return (
      <ModalCenter ref={innerRef} title={t('shippedPayment.title')} width="lg" type="default" fullScreenMobile={true}>
        <div className="flex flex-col h-full">
          <div className="flex-1 flex flex-col gap-4 py-4 pb-24 md:pb-4">
            <div>
              <label className="text-sm font-medium">
                {t('shippedPayment.total')} <span className="text-error">*</span>
              </label>
              <Input
                type="number"
                min="0"
                step="0.01"
                className="mt-1"
                value={total}
                onChange={(e) => setTotal(e.target.value)}
                placeholder={t('shippedPayment.totalPlaceholder')}
              />
            </div>
            <div>
              <label className="text-sm font-medium">
                {t('shippedPayment.paymentMethod')} <span className="text-error">*</span>
              </label>
              <Select value={paymentMethodId} onValueChange={setPaymentMethodId} disabled={paymentMethodsLoading}>
                <SelectTrigger className="w-full mt-1">
                  <SelectValue placeholder={t('shippedPayment.paymentMethodPlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  {paymentMethods.map((method) => (
                    <SelectItem key={method.id} value={String(method.id)}>
                      {method.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">
                {t('shippedPayment.reason')} <span className="text-error">*</span>
              </label>
              <TextArea
                className="mt-1 min-h-[96px] resize-none"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={t('shippedPayment.reasonPlaceholder')}
              />
            </div>
          </div>
          <div className="fixed bottom-0 left-0 right-0 p-4 md:pb-0 md:px-0 md:relative md:border-t-0">
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="lg" onClick={handleClose}>
                {t('shippedPayment.cancel')}
              </Button>
              <Button variant="primary" size="lg" onClick={handleSave} disabled={isSubmitting || !canSubmit}>
                {isSubmitting ? t('shippedPayment.submitting') : t('shippedPayment.save')}
              </Button>
            </div>
          </div>
        </div>
      </ModalCenter>
    );
  },
);

ShippedPaymentModal.displayName = 'ShippedPaymentModal';
